/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { JSX } from "react";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { colors, spacing } from "../../../../../global/emotion";
import { stylesOpacity } from "../../../../../global/emotion/styles/stylesOpacity";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../../types/keyedElement.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { Text } from "../../_core/Text/Text.emotion";
import type { IconElement } from "../../icons/Icon/Icon";

const hallowElement: HallowElement = "div";

const determinedElementStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: colors.transparent,
  borderRadius: spacing.m,
  color: theme.semanticColors.onOverlayMedHigh,
  cursor: "pointer",
  display: "flex",
  gap: spacing.m,
  padding: spacing.m,
  transition: "all 150ms ease-out",
});
const disabledStyle: Interpolation = {
  cursor: "pointer",
  opacity: "50%",
};
const rightActionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
};
const selectedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayLow,
  color: theme.semanticColors.onOverlayHighest,
});
const statesStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: colors.transparent,
  color: theme.semanticColors.onOverlayMedHigh,
  ":hover": { backgroundColor: theme.semanticColors.onOverlayLow },
  ":active": {
    backgroundColor: theme.semanticColors.onOverlayLow,
    color: theme.semanticColors.onOverlayHighest,
  },
});
const textContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.t,
  minWidth: spacing.none,
  width: spacing.full,
};

export type CellProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  disabled?: boolean;
  icon?: IconElement;
  label?: string;
  rightActions?: KeyedElement | KeyedElement[];
  selected?: boolean;
  text?: string;
};

export const Cell = forwardRef<any, CellProps>(
  (
    {
      asChild = false,
      children,
      disabled,
      icon,
      label,
      rightActions,
      selected,
      text,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        icon ? icon : null,
        <div key="textContainer" css={textContainerStyle}>
          {label && (
            <Text overflow="ellipsis" size="l" type="detail">
              {label}
            </Text>
          )}
          {text && (
            <Text overflow="ellipsis" size="m" type="title">
              {text}
            </Text>
          )}
        </div>,
        rightActions ? (
          <div key="rightActions" css={rightActionsStyle}>
            {rightActions}
          </div>
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[
          determinedElementStyle,
          stylesOpacity.active,
          stylesOpacity.base,
          disabled ? disabledStyle : null,
          selected ? selectedStyle : null,
          !disabled && !selected ? statesStyle : null,
        ]}
        {...props}
      >
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

Cell.displayName = "Cell";
